import { Dialog, DialogTitle, DialogContent, DialogActions, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import ErrorAlert from "../common/ErrorAlert";
import React from "react";
import { RejectReasonDto } from "../../models/medicalCheckups/rejectReasonDto";
import TmFullButton from "../common/buttons/TmFullButton";
import TmEmptyButton from "../common/buttons/TmEmptyButton";
import RejectReasonsAutocompleteFormControl from "../common/form/RejectReasonsAutocompleteFormControl";
import { TextAreaFormControl } from "../common/form/TextAreaFormControl";

interface NotAllowedDialogProps {
    medicalCheckupId: number;
    open: boolean;
    onSubmit: (selectedReasons: number[], comment: string) => void;
    onClose: () => void;
}

interface NotAllowedFormData {
    rejectReasons: RejectReasonDto[];
    comment: string;
}

export const NotAllowedDialog: React.FC<NotAllowedDialogProps> = ({ open, onSubmit, onClose }) => {
    const [error, setError] = useState('');

    const methods = useForm<NotAllowedFormData>({
        defaultValues: {
            rejectReasons: [],
            comment: ''
        },
        mode: 'onChange'
    });

    const { watch, handleSubmit, reset } = methods;
    const watchedRejectReasons = watch('rejectReasons') || [];

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (!open) {
            reset();
        }
    }, [open, reset]);

    // Clear comment field when no reject reasons are selected
    useEffect(() => {
        if (watchedRejectReasons.length === 0) {
            reset();
        }
    }, [watchedRejectReasons.length, reset]);

    // Check if "Другое" reason is selected
    const hasOtherReason = Array.isArray(watchedRejectReasons) && watchedRejectReasons.length > 0 
        ? watchedRejectReasons.some(reason => reason.name === "Другое")
        : false;

    // Check if save button should be disabled
    const watchedComment = watch('comment');
    const isSaveDisabled = !Array.isArray(watchedRejectReasons) || watchedRejectReasons.length === 0 || (hasOtherReason && !watchedComment.trim());

    const onSubmitForm = (data: NotAllowedFormData) => {
        const selectedReasons = data.rejectReasons.map(r => r.id);
        onSubmit(selectedReasons, data.comment);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            sx={{
                '& .MuiDialog-paper': {
                    height: '80vh',
                    maxHeight: '90vh',
                }
            }}
        >
            <DialogTitle variant="h5" fontWeight="600" sx={{ textAlign: 'center' }}>
                {"Недопуск сотрудника"}
            </DialogTitle>
            <Typography textAlign="center" fontWeight="600">
                Укажите причины недопуска сотрудника
            </Typography>

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmitForm)}>
                    <DialogContent>
                        <RejectReasonsAutocompleteFormControl
                            name="rejectReasons"
                            label="Причина недопуска"
                            required
                        />

                        <Typography>
                            Укажите дополнительные причины недопуска, которых нет в списке
                        </Typography>

                        <TextAreaFormControl
                            name="comment"
                            label="Комментарий"
                            rows={8}
                            style={{ width: '100%', marginTop: 1 }}
                            required={hasOtherReason}
                            validateConditionally={(value, formValues) => {
                                // Check if "Другое" reason is selected in the current form state
                                const rejectReasons = formValues.rejectReasons as RejectReasonDto[] || [];
                                const hasOther = Array.isArray(rejectReasons) && rejectReasons.length > 0 
                                    ? rejectReasons.some(reason => reason.name === "Другое")
                                    : false;
                                // Only require comment if "Другое" is selected
                                if (!hasOther) {
                                    return true; // Validation passes
                                }
                                // If "Другое" is selected, comment is required
                                return (value && value.trim().length > 0) || 'Поле обязательно для заполнения';
                            }}
                        />
                    </DialogContent>

                    <DialogActions style={{ justifyContent: 'center' }}>
                        <TmEmptyButton onClick={onClose} width="45%">
                            Отмена
                        </TmEmptyButton>
                        <TmFullButton
                            onClick={handleSubmit(onSubmitForm)}
                            width="45%"
                            disabled={isSaveDisabled}
                        >
                            Сохранить
                        </TmFullButton>
                    </DialogActions>
                </form>
            </FormProvider>

            {error && (
                <ErrorAlert
                    message={error}
                    onClose={() => setError('')}
                />
            )}
        </Dialog>
    );
};